import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Defer cookie banner display so it doesn't become the LCP element
    const timer = setTimeout(() => {
      const consent = localStorage.getItem('cookie_consent');
      if (!consent) {
        setIsVisible(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', 'all');
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem('cookie_consent', 'essential');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-card/95 backdrop-blur-lg border-t border-border/50 shadow-2xl"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="container mx-auto max-w-4xl">
        <div className="flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0" aria-hidden="true">
              <Cookie className="w-5 h-5" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 id="cookie-banner-title" className="font-semibold text-foreground mb-1">
                {t('common.cookieBannerTitle')}
              </h2>
              <p id="cookie-banner-description" className="text-sm text-muted-foreground leading-relaxed">
                {t('common.cookieBannerDescription')}
              </p>
              <div className="flex gap-3 mt-2 text-xs">
                <Link to="/cookies" className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded">
                  {t('common.cookieNotice')}
                </Link>
                <span className="text-muted-foreground" aria-hidden="true">|</span>
                <Link to="/privacy" className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded">
                  {t('common.privacyPolicy')}
                </Link>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center shrink-0">
            <Button 
              variant="outline" 
              size="default" 
              onClick={handleRejectNonEssential}
              className="text-sm min-h-[44px]"
            >
              {t('common.rejectNonEssential')}
            </Button>
            <Button 
              variant="default" 
              size="default" 
              onClick={handleAcceptAll}
              className="text-sm min-h-[44px]"
            >
              {t('common.acceptAll')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
